// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import { dumbbell, lineChart, rankedBars, stackedArea } from './charts';
import { card, chartBox, legend, note, stats, viewHead } from './ui';
import { gloss } from '../glossary';
import { esc, money, num, pct, period, weeks } from '../format';
import { PHYSICAL, PSYCH, divisionColor } from '../colors';
import { totalOf, withDivision } from '../data';
import { openIndustryDrawer } from './industryDrawer';
import type { Bundle, Val } from '../types';

export const title = 'Psychological';

export function render(b: Bundle): string {
  const { claims } = b;
  const P = claims.periods;
  const n = P.length;
  const total = totalOf(claims.nature)!;
  const mental = claims.nature.find((r) => r.code === 'I')!;
  const mechMental = claims.mechanism.find((r) => r.code === '8');

  const share: Val[] = P.map((_, i) =>
    total.count[i] && mental.count[i] !== null ? (mental.count[i]! / total.count[i]!) * 100 : null,
  );

  const compIdx = (() => { for (let i = n - 1; i >= 0; i--) if (mental.comp?.[i] !== null && total.comp?.[i] !== null) return i; return n - 2; })();
  const lostIdx = (() => { for (let i = n - 1; i >= 0; i--) if (mental.lost?.[i] !== null && total.lost?.[i] !== null) return i; return n - 2; })();

  // Physical injuries = the "0 Injuries" major group, the honest comparator for
  // "a mental health claim against a broken arm".
  const physical = claims.nature.find((r) => r.code === '0')!;

  // NOTE: nature of injury is not crossed with occupation OR industry anywhere
  // in this release — Tables 2.x are all-cause by occupation, and there is no
  // mental-stress-by-occupation table. So the two charts below are all-cause,
  // and their copy says so rather than implying a psychological split.
  const occ = claims.occupation.filter((r) => r.level === 2 && !r.total && !r.nfd);
  const industries = withDivision(claims.industry);

  const growth = mental.count[0] !== null && mental.count[n - 1] !== null
    ? ((mental.count[n - 1]! - mental.count[0]!) / mental.count[0]!) * 100 : null;
  const growthAll = total.count[0] !== null && total.count[n - 1] !== null
    ? ((total.count[n - 1]! - total.count[0]!) / total.count[0]!) * 100 : null;

  const stackSeries = [
    { label: 'Physical injuries and other conditions', color: PHYSICAL, values: P.map((_, i) => (total.count[i] ?? 0) - (mental.count[i] ?? 0)) },
    { label: 'Mental health conditions', color: PSYCH, values: P.map((_, i) => mental.count[i] ?? 0) },
  ];

  return `
    ${viewHead(
      'Psychological injury',
      'How much of Australia’s workplace harm is now psychological?',
      `A ${gloss('mental health condition', 'mental health condition')} claim is the fastest-growing and by a wide margin the most expensive kind of serious workers’ compensation claim in Australia. This view sets it against everything else, on both money and time.`,
    )}

    ${stats([
      { value: num(mental.count[n - 1]), label: `Psychological claims in ${period(P[n - 1])}`, sub: `up from ${num(mental.count[0])} in ${period(P[0])}`, tone: 'psych' },
      { value: growth === null ? '—' : `+${growth.toFixed(0)}%`, label: 'Growth over the decade', sub: growthAll === null ? '' : `all serious claims grew +${growthAll.toFixed(0)}%`, tone: 'psych' },
      { value: pct(share[n - 1], 2), label: 'Share of all serious claims', sub: `was ${pct(share[0], 2)} a decade ago` },
      { value: money(mental.comp?.[compIdx] ?? null), label: 'Median payment', sub: `${(mental.comp![compIdx]! / total.comp![compIdx]!).toFixed(2)}× the typical claim, ${period(P[compIdx])}`, tone: 'warn' },
      { value: weeks(mental.lost?.[lostIdx] ?? null), label: 'Median time off work', sub: `${(mental.lost![lostIdx]! / total.lost![lostIdx]!).toFixed(2)}× the typical claim`, tone: 'warn' },
      { value: num(mechMental?.count[n - 1] ?? null), label: `Claims caused by ${gloss('mental stress', 'mental stress')}`, sub: 'the mechanism, not the diagnosis' },
    ])}

    ${card(
      'The share that is psychological',
      'Serious claims split into mental health conditions and everything else. The absolute number of physical injuries has grown too — this is not a story about one replacing the other, it is a story about one growing four times faster.',
      chartBox(
        stackedArea({
          series: stackSeries,
          labels: P.map(period),
          width: 840,
          height: 300,
          fmtY: (v) => `${(v / 1000).toFixed(0)}k`,
          tip: (i) =>
            `${period(P[i])}\nMental health conditions: ${num(mental.count[i])} (${pct(share[i], 2)})\nEverything else: ${num((total.count[i] ?? 0) - (mental.count[i] ?? 0))}\nAll serious claims: ${num(total.count[i])}`,
        }),
        'wide',
      ) + legend(stackSeries.map((s) => ({ color: s.color, label: s.label }))),
    )}

    ${card(
      'What one claim costs, side by side',
      `Median compensation paid and median working weeks lost, mental health conditions against physical injuries. Both series stop before ${period(P[n - 1])}: the newest year is withheld on every cost measure because compensation is still accruing.`,
      `<div class="split2">
        <div>
          <p class="sub-h">Money</p>
          ${chartBox(
            lineChart({
              series: [
                { values: physical.comp ?? [], color: PHYSICAL, label: 'Physical injuries', width: 2.4 },
                { values: mental.comp ?? [], color: PSYCH, label: 'Mental health conditions', width: 2.4 },
              ],
              labels: P.map(period),
              domain: [0, Math.max(...(mental.comp ?? []).filter((v): v is number => v !== null)) * 1.15],
              width: 460,
              height: 250,
              fmtY: (v) => `$${(v / 1000).toFixed(0)}k`,
              everyX: 3,
              tip: (i) => `${period(P[i])}\nMental health: ${money(mental.comp?.[i] ?? null)}\nPhysical injuries: ${money(physical.comp?.[i] ?? null)}`,
            }),
          )}
        </div>
        <div>
          <p class="sub-h">Time</p>
          ${chartBox(
            lineChart({
              series: [
                { values: physical.lost ?? [], color: PHYSICAL, label: 'Physical injuries', width: 2.4 },
                { values: mental.lost ?? [], color: PSYCH, label: 'Mental health conditions', width: 2.4 },
              ],
              labels: P.map(period),
              domain: [0, Math.max(...(mental.lost ?? []).filter((v): v is number => v !== null)) * 1.15],
              width: 460,
              height: 250,
              fmtY: (v) => `${v.toFixed(0)}w`,
              everyX: 3,
              tip: (i) => `${period(P[i])}\nMental health: ${weeks(mental.lost?.[i] ?? null)}\nPhysical injuries: ${weeks(physical.lost?.[i] ?? null)}`,
            }),
          )}
        </div>
      </div>` +
        legend([
          { color: PSYCH, label: 'Mental health conditions' },
          { color: PHYSICAL, label: 'Physical injuries' },
        ]),
    )}

    ${card(
      `Which occupations lose the most time, ${period(P[lostIdx])}`,
      `Both dots are working weeks. The pale one is the ${weeks(total.lost?.[lostIdx] ?? null)} median across all occupations, the dark one is this occupation's — so the gap is how much longer than the national median its injured workers stay off. Hover any row for that occupation's median payout as well. (Money and weeks cannot share an axis; they differ by four orders of magnitude.)`,
      chartBox(
        dumbbell({
          rows: occ
            .filter((r) => r.lost?.[lostIdx] !== null && r.lost?.[lostIdx] !== undefined)
            .sort((a, b) => (b.lost![lostIdx] as number) - (a.lost![lostIdx] as number))
            .slice(0, 14)
            .map((r) => ({
              label: r.name,
              a: total.lost?.[lostIdx] ?? null,
              b: r.lost?.[lostIdx] ?? null,
              tip: `${r.name}\nMedian time lost: ${weeks(r.lost?.[lostIdx] ?? null)}\nAll occupations: ${weeks(total.lost?.[lostIdx] ?? null)}\nMedian compensation: ${money(r.comp?.[lostIdx] ?? null)}\n${num(r.count[lostIdx])} serious claims`,
            })),
          domain: [0, Math.max(...occ.map((r) => (r.lost?.[lostIdx] as number) || 0)) * 1.1],
          colorA: PHYSICAL,
          colorB: PSYCH,
          width: 760,
          fmt: (v) => `${v.toFixed(0)}w`,
        }),
        'wide',
      ) +
        legend([
          { color: PHYSICAL, label: 'Median across all occupations' },
          { color: PSYCH, label: 'This occupation' },
        ]),
    )}

    ${card(
      `Which industries carry the most serious claims, ${period(P[n - 1])}`,
      `Safe Work Australia never publishes nature of injury crossed with industry, so a per-industry psychological count does not exist anywhere in this release. Rather than invent one, this ranks industries by <em>all</em> serious claims. Click any industry for its full record.`,
      rankedBars(
        industries
          .filter((r) => r.count[n - 1] !== null)
          .sort((a, b) => (b.count[n - 1] as number) - (a.count[n - 1] as number))
          .slice(0, 12)
          .map((r) => ({
            label: r.name,
            value: r.count[n - 1],
            display: num(r.count[n - 1]),
            color: divisionColor(r.division),
            sub: r.divisionName,
            tip: `${r.name}\n${num(r.count[n - 1])} serious claims of all kinds in ${period(P[n - 1])}\nClick for the full record`,
            href: `#/psych&i=${encodeURIComponent(r.code)}`,
          })),
      ) +
        note(
          `<strong>Why this chart is not about psychological claims.</strong> Nature of injury and industry are published as two separate tables with no link between them, so the release contains no per-industry psychological count. The bars above are therefore all serious claims of every kind — which is what their tooltips say — and no split of them into physical and psychological is possible from this data.`,
          'warn',
        ),
    )}

    ${note(
      `<strong>What changed in the law.</strong> Model work health and safety regulations covering psychosocial hazards — workload, bullying, exposure to trauma, poor support — commenced across most Australian jurisdictions from 2022 onwards. The growth shown here predates them by most of a decade. Whether the duties bend the curve will not be visible in this data for several more releases.`,
    )}
  `;
}

export function mount(root: HTMLElement, b: Bundle): void {
  root.querySelectorAll<HTMLAnchorElement>('a.bar-row[href*="&i="]').forEach((a) => {
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const code = decodeURIComponent(a.getAttribute('href')!.split('&i=')[1] ?? '');
      const row = withDivision(b.claims.industry).find((r) => r.code === code);
      if (row) openIndustryDrawer(b, row);
    });
  });
  void esc;
}
